import { Heading } from '@assembly-js/design-system';
import { MenuItem } from '@mui/material';
import clsx from 'clsx';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';

import { TextField } from 'src/components/TextField';
import { CurrencyTextField } from 'src/components/textfield/CurrencyTextField';
import { PriceTypeToggle } from 'src/routes/payments/services/components/PriceTypeToggle';
import {
  OneTimePriceSchema,
  RecurringPriceSchema,
  ServiceSchema,
} from 'src/routes/payments/services/types';
import { SubscriptionInterval } from 'src/routes/payments/subscriptions/types/subscriptions';
import { getRenderCurrencyPrefixProps } from 'src/routes/payments/utils/currencyInputUtils';
import { BILLING_PERIODS, PriceType } from 'src/services/api/productsApi';
import { GetCurrencyPrefix } from 'src/utils/PaymentsUtils';

const isRecurringPriceTouched = (
  _touched: FormikTouched<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _touched is FormikTouched<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

const isRecurringPriceError = (
  _errors: FormikErrors<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _errors is FormikErrors<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

export const PriceInfo = ({
  defaultCurrency,
  showRecurringField,
  toggleLabel,
  toggleShouldBeShown,
  size,
  selectedInterval,
}: {
  defaultCurrency: string;
  showRecurringField?: boolean;
  toggleLabel?: string;
  toggleShouldBeShown?: boolean;
  size?: 'lg' | 'xl';
  /**
   * In subscription mode, once the user selects a price interval (e.g., monthly) for the first product,
   * all subsequently added products must follow the same interval. For this reason, the interval
   * dropdown is disabled to prevent changing it.
   */
  selectedInterval?: SubscriptionInterval;
}) => {
  const prefix = GetCurrencyPrefix({ currency: defaultCurrency });
  const { touched, handleBlur, handleChange, errors, values, setFieldValue } =
    useFormikContext<ServiceSchema>();

  const isRecurringPrice = (
    price: OneTimePriceSchema | RecurringPriceSchema,
  ): price is RecurringPriceSchema => {
    return price?.type === PriceType.Recurring;
  };

  const getIntervalValue = (
    price: OneTimePriceSchema | RecurringPriceSchema | undefined,
  ) => {
    if (price && isRecurringPrice(price)) {
      return price.interval;
    }
    return '';
  };

  const price = values.prices.at(0);
  const priceErrorContainer = errors?.prices?.[0];
  const amountErrorHelperText =
    !!touched?.prices?.[0]?.amount &&
    typeof priceErrorContainer !== 'string' &&
    priceErrorContainer?.amount;

  const isBillingPeriodTouched =
    touched.prices &&
    touched?.prices?.[0] &&
    isRecurringPriceTouched(touched.prices[0], price) &&
    touched.prices[0].interval;

  const billingPeriodErrorHelperText =
    isBillingPeriodTouched &&
    typeof priceErrorContainer !== 'string' &&
    typeof priceErrorContainer !== 'undefined' &&
    isRecurringPriceError(priceErrorContainer, price) &&
    priceErrorContainer?.interval;

  const onToggleType = (tab: PriceType) => {
    setFieldValue('prices.[0].type', tab);
    if (tab === PriceType.Recurring) {
      setFieldValue('prices.[0].interval', SubscriptionInterval.Month);
    } else {
      setFieldValue('prices.[0].interval', undefined);
    }
  };

  return (
    <div>
      {toggleShouldBeShown && (
        <div className="flex justify-between items-center mb-4">
          <Heading size={size} className=" text-black-heading">
            {toggleLabel}
          </Heading>
          {price && (
            <PriceTypeToggle type={price.type} onToggleType={onToggleType} />
          )}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div>
          <Heading className="mb-1 text-black-heading">Price</Heading>
          <CurrencyTextField
            name="prices[0].amount"
            fullWidth
            sizeVariant="medium"
            onBlur={handleBlur}
            onChange={handleChange}
            value={price?.amount || ''}
            InputProps={getRenderCurrencyPrefixProps(prefix || '')}
            variant="outlined"
            error={Boolean(
              touched.prices?.[0]?.amount && amountErrorHelperText,
            )}
            helperText={amountErrorHelperText}
            currency={defaultCurrency}
          />
        </div>
        {showRecurringField && price?.type === PriceType.Recurring && (
          <div>
            <Heading className="text-black-heading mb-1">
              Billing period
            </Heading>
            <TextField
              select
              sizeVariant="standard"
              name="prices[0].interval"
              variant="outlined"
              fullWidth
              className={clsx(
                '[&>div>svg]:h-4 [&>div>svg]:w-4 [&>div>svg]:mt-1.5 ',
                {
                  '[&>fieldset]:!border-non-hover-border':
                    Boolean(selectedInterval),
                },
              )}
              additionalSelectProps={{
                classes: {
                  select: 'text-base text-black-heading font-normal',
                  disabled: 'text-hover-border',
                },
              }}
              value={getIntervalValue(price)}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                isBillingPeriodTouched && billingPeriodErrorHelperText,
              )}
              helperText={billingPeriodErrorHelperText}
              disabled={Boolean(selectedInterval)}
            >
              {BILLING_PERIODS.map((i) => (
                <MenuItem value={i.value} key={i.value}>
                  {i.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}
      </div>
    </div>
  );
};
